import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { PATTERNS } from '../../../utils/constans/constans';
import useDefaultStockSWR from '../../../hooks/useDefaultStock';
import { SubmitHandler, useForm } from 'react-hook-form';
import PrimaryBtn from '../../shared/Buttons/PrimaryButton';
import {
    AdminUserProfileRequest,
    ProfileFormProps,
    AdminUserProfileFormValues,
} from '../../../models/User';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import useProfileEditorDialog from '../../../hooks/useProfileEditorDialog';
import React, { useState } from 'react';
import {
    Box,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import * as yup from 'yup';
import TextFieldStyled from '../../shared/TextFieldStyled';
import { FormControlsWrapper } from '../../styled/Form/FormControlsWrapper';
import SelectStyled from '../../shared/SelectStyled';
import useAdminApi from '../../../hooks/admin/useAdminApi';
import { ProfileLoadingLayout } from '../../layouts/LoadingLayouts/ProfileLoadingLayout';
import { ErrorTypography } from '../../styled/ErrorTypography';

const schema = yup.object().shape({
    email: yup
        .string()
        .required('FORM_VALIDATION_ERROR.REQUIRED')
        .matches(PATTERNS.VALID_EMAIL, 'FORM_VALIDATION_ERROR.EMAIL'),
    first_name: yup
        .string()
        .required('FORM_VALIDATION_ERROR.REQUIRED')
        .min(3, 'FORM_VALIDATION_ERROR.MIN_CHARACTER_3'),
    last_name: yup
        .string()
        .required('FORM_VALIDATION_ERROR.REQUIRED')
        .min(3, 'FORM_VALIDATION_ERROR.MIN_CHARACTER_3'),
    company: yup
        .string()
        .required('FORM_VALIDATION_ERROR.REQUIRED')
        .min(3, 'FORM_VALIDATION_ERROR.MIN_CHARACTER_3'),
    bc_customer_no: yup.string().required('FORM_VALIDATION_ERROR.REQUIRED'),
    phone: yup.lazy((value) =>
        !value
            ? yup.string()
            : yup
                  .string()
                  .matches(PATTERNS.PHONE_NUMBER, 'FORM_VALIDATION_ERROR.PHONE')
    ),
    tax_number: yup.string().required('FORM_VALIDATION_ERROR.REQUIRED'),
    default_stock: yup.string().required('FORM_VALIDATION_ERROR.REQUIRED'),
});

export const UserProfileEdit: React.FC<ProfileFormProps> = (props) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<AdminUserProfileFormValues>({
        resolver: yupResolver(schema),
    });
    const profileDialog = useProfileEditorDialog();
    const user = profileDialog.selectedUserStates;

    const { stocks, isLoadingStock, isErrorStock } = useDefaultStockSWR();
    const admin = useAdminApi();
    const { t } = useTranslation();

    const [submitIsLoading, setSubmitIsLoading] = useState(false);
    const [defaultStock, setDefaultStock] = useState(
        user ? user.default_stock : ''
    );
    const [activeStatus, setActiveStatus] = useState(
        user && user.active ? 'active' : 'inactive'
    );

    const makeRequestBody = (data): AdminUserProfileRequest => {
        return {
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
            company: data.company,
            phone: data.phone,
            tax_number: data.tax_number,
            default_stock: data.default_stock,
            bc_customer_no: data.bc_customer_no,
            active: data.active == 'active',
            alert_message: data.alert_message,
            default_profit_margin: user.default_profit_margin, // TODO: ha szükséges hozzá kell adni a formhoz!
        };
    };

    const onSubmit: SubmitHandler<AdminUserProfileFormValues> = (data) => {
        setSubmitIsLoading(true);
        admin
            .updateUser(makeRequestBody(data), user.id)
            .then(() => {
                SnackbarUtils.success(
                    t('NOTIFICATION.PROFILE_EDITED_SUCCESSFULLY').toString()
                );
                // mutate();
                props.submitSuccess();
            })
            .catch(() => {
                setSubmitIsLoading(false);
            });
    };

    const getTranslate = (key: string) => {
        return t(key).toString();
    };

    const handleChange = (event: SelectChangeEvent) => {
        setDefaultStock(event.target.value as string);
    };
    const handleActiveChange = (event: SelectChangeEvent) => {
        setActiveStatus(event.target.value as string);
    };

    if (isLoadingStock) {
        return <ProfileLoadingLayout />;
    }

    if (!user || isErrorStock) {
        return <ErrorTypography errorCode={'GLOBAL_ERROR'} />;
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <>
                    {/* BC code */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.BC_CODE'
                            ).toString()}
                            defaultValue={user.bc_customer_no}
                            {...register('bc_customer_no')}
                            helperText={' '}
                        />
                    </FormControl>
                    {/* Email address */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            type="email"
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.EMAIL'
                            ).toString()}
                            defaultValue={user.email}
                            {...register('email')}
                            helperText={' '}
                        />
                    </FormControl>

                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.COMPANY'
                            ).toString()}
                            defaultValue={user.company}
                            error={!!errors.company}
                            {...register('company')}
                            helperText={
                                errors.company
                                    ? getTranslate(errors.company.message)
                                    : ' '
                            }
                        />
                    </FormControl>

                    <FormControlsWrapper>
                        {/* Last Name */}
                        <FormControl fullWidth>
                            <TextFieldStyled
                                label={t(
                                    'PROFILE.PROFILE_DIALOG.FORM_CONTENT.LAST_NAME'
                                ).toString()}
                                defaultValue={user.last_name}
                                error={!!errors.last_name}
                                {...register('last_name')}
                                helperText={
                                    errors.last_name
                                        ? getTranslate(errors.last_name.message)
                                        : ' '
                                }
                            />
                        </FormControl>
                        {/* First Name */}
                        <FormControl fullWidth>
                            <TextFieldStyled
                                label={t(
                                    'PROFILE.PROFILE_DIALOG.FORM_CONTENT.FIRST_NAME'
                                ).toString()}
                                defaultValue={user.first_name}
                                error={!!errors.first_name}
                                {...register('first_name')}
                                helperText={
                                    errors.first_name
                                        ? getTranslate(
                                              errors.first_name.message
                                          )
                                        : ' '
                                }
                            />
                        </FormControl>
                    </FormControlsWrapper>
                    {/* Phone */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.PHONE'
                            ).toString()}
                            defaultValue={user.phone}
                            error={!!errors.phone}
                            {...register('phone')}
                            helperText={
                                errors.phone
                                    ? getTranslate(errors.phone.message)
                                    : ' '
                            }
                        />
                    </FormControl>
                    {/* Tax number */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.TAX_NUMBER'
                            ).toString()}
                            defaultValue={user.tax_number}
                            error={!!errors.tax_number}
                            {...register('tax_number')}
                            helperText={
                                errors.tax_number
                                    ? getTranslate(errors.tax_number.message)
                                    : ' '
                            }
                        />
                    </FormControl>
                    {/* Default stock */}
                    <FormControl fullWidth variant="filled">
                        <InputLabel
                            sx={{
                                color: '#999999',
                            }}
                        >
                            {t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.DEFAULT_STOCK'
                            ).toString()}
                        </InputLabel>
                        <SelectStyled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.DEFAULT_STOCK'
                            ).toString()}
                            {...register('default_stock', {
                                onChange: (e) => handleChange(e),
                            })}
                            value={defaultStock}
                        >
                            {stocks.map((stock) => (
                                <MenuItem
                                    key={stock.bc_location_code}
                                    value={stock.bc_location_code}
                                >
                                    {stock.bc_location_name}
                                </MenuItem>
                            ))}
                        </SelectStyled>
                        <FormHelperText error={!!errors.default_stock}>
                            &#8203;
                            {errors.default_stock &&
                                errors.default_stock.message}
                        </FormHelperText>
                    </FormControl>
                    {/* ACTIVE */}
                    <FormControl fullWidth variant="filled">
                        <InputLabel
                            sx={{
                                color: '#999999',
                            }}
                        >
                            {t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.ACTIVE'
                            ).toString()}
                        </InputLabel>
                        <SelectStyled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.ACTIVE'
                            ).toString()}
                            {...register('active', {
                                onChange: (e) => handleActiveChange(e),
                            })}
                            value={activeStatus}
                        >
                            <MenuItem value={'active'}>
                                {t(
                                    'PROFILE.PROFILE_DIALOG.FORM_CONTENT.STATUS.ACTIVE'
                                )}
                            </MenuItem>
                            <MenuItem value={'inactive'}>
                                {t(
                                    'PROFILE.PROFILE_DIALOG.FORM_CONTENT.STATUS.INACTIVE'
                                )}
                            </MenuItem>
                        </SelectStyled>
                        <FormHelperText error={!!errors.active}>
                            &#8203;
                            {errors.active && errors.active.message}
                        </FormHelperText>
                    </FormControl>
                    {/* Note */}
                    {activeStatus == 'inactive' && (
                        <>
                            <Typography paragraph>
                                {t(
                                    'PROFILE.PROFILE_DIALOG.FORM_CONTENT.ALERT_MESSAGE'
                                ).toString()}
                            </Typography>
                            <FormControl fullWidth>
                                <TextFieldStyled
                                    label={t(
                                        'PROFILE.PROFILE_DIALOG.FORM_CONTENT.ALERT_MESSAGE'
                                    ).toString()}
                                    multiline
                                    rows={4}
                                    defaultValue={user.alert_message}
                                    {...register('alert_message')}
                                />
                            </FormControl>
                        </>
                    )}
                </>
                <Box
                    sx={{
                        justifyContent: 'space-between',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <PrimaryBtn type="submit" disabled={submitIsLoading}>
                            {t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.SEND'
                            ).toString()}
                        </PrimaryBtn>
                    </Box>
                </Box>
            </form>
        </>
    );
};
